import React, { useMemo } from "react";
import { Pagination as BsPagination } from "react-bootstrap";

interface PaginationProps {
  rows: Array<any>;
  pageSize: number;
  activePageNumber: number;
  setActivePageNumber: Function;
  maxPagesToShow?: number;
}

function Pagination({
  rows,
  pageSize,
  activePageNumber,
  setActivePageNumber,
  maxPagesToShow = 5,
}: PaginationProps) {
  const numberOfPages = useMemo(
    () => calculateNumberOfPages(rows, pageSize),
    [rows, pageSize]
  );

  const pagesListToShow = useMemo(
    () =>
      calculateCurrentPages(activePageNumber, numberOfPages, maxPagesToShow),
    [numberOfPages, activePageNumber, maxPagesToShow]
  );

  return (
    <BsPagination>
      {pagesListToShow.map((pageNumber) => {
        return (
          <>
            {showsBack(pageNumber, pagesListToShow) && (
              <BsPagination.Prev
                onClick={() => setActivePageNumber(activePageNumber - 1)}
              />
            )}
            <div key={pageNumber}>
              <BsPagination.Item
                active={pageNumber === activePageNumber}
                onClick={() => setActivePageNumber(pageNumber)}
              >
                {pageNumber}
              </BsPagination.Item>
            </div>
            {showsNext(pageNumber, pagesListToShow, numberOfPages) && (
              <BsPagination.Next
                onClick={() => setActivePageNumber(activePageNumber + 1)}
              />
            )}
          </>
        );
      })}
    </BsPagination>
  );
}

const showsNext = (
  pageNumber: number,
  currentPagesList: number[],
  totalPages: number
): boolean => {
  if (pageNumber === totalPages) {
    return false;
  }
  return pageNumber === currentPagesList[currentPagesList.length - 1];
};

const showsBack = (
  pageNumber: number,
  currentPagesList: number[]
): boolean => {
  if (pageNumber === 1) {
    return false;
  }
  return pageNumber === currentPagesList[0];
};

const calculateNumberOfPages = (rows: Array<any>, pageLength: number) => {
  const intPart = Math.trunc(rows.length / pageLength);
  const remainder = rows.length % pageLength;
  if (remainder !== 0) {
    return intPart + 1;
  }
  return intPart;
};

export const calculateCurrentPages = (
  currentPageNumber: number,
  totalPages: number,
  maxNumberOfPages: number
): number[] => {
  if (maxNumberOfPages === 1) {
    return [currentPageNumber];
  }

  if (maxNumberOfPages >= totalPages) {
    return new Array(totalPages).fill(0).map((_, index) => index + 1);
  }

  const maxIsPair = maxNumberOfPages % 2 === 0;
  const radius = (maxNumberOfPages - 1) / 2;
  const leftRadius = maxIsPair ? radius - 1 : radius;

  let minPage = Math.max(currentPageNumber - leftRadius, 1);
  let maxPage = Math.min(minPage + (maxNumberOfPages - 1), totalPages);

  if (maxPage < currentPageNumber + radius) {
    minPage = Math.max(maxPage - (maxNumberOfPages - 1), 1);
  }

  let i = minPage;
  let currentPages = [];
  while (i <= maxPage) {
    currentPages.push(i);
    i++;
  }
  return currentPages;
};

export default Pagination;
